import React, {
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useId,
  type CSSProperties,
} from "react";
import Select from "react-dropdown-select";
import type { SelectChangeEvent } from "@mui/material/Select";
import { tokens } from "../design-system/tokens";
import styles from "./SelectBox.module.css";

const SelectAny: any = Select;

export type SelectBoxSize = "small" | "medium" | "large";
export type SelectBoxColor =
  | "secondary"
  | "outline"
  | "outlineLight"
  | "outlineSecondary";

function toCssLength(value: number | string | undefined): string | undefined {
  if (value === undefined || value === "") return undefined;
  if (typeof value === "number") return `${value}px`;
  return value;
}

export interface Option {
  value: string;
  label: string;
}

export interface SelectBoxProps {
  value: string;
  onChange: (event: SelectChangeEvent<string>) => void;
  options: Option[];
  /** Helper text shown above the select (same pattern as `UiElements/Input`). */
  label?: string;
  /** Merged onto the outer wrapper (e.g. `marginTop`). */
  style?: React.CSSProperties;
  name?: string;
  disabled?: boolean;
  className?: string;
  /** Applied from 768px up. Omitted on smaller viewports unless `mobileWidth` is set (same as `UiElements/Input`). */
  width?: string | number;
  /** Overrides default full-width on viewports below 768px (default is `100%`). */
  mobileWidth?: string | number;
  size?: SelectBoxSize;
  color?: SelectBoxColor;
  rtl?: boolean;
  placeholder?: string;
  loading?: boolean;
  maxWidth?: string | number;
  dropdownMaxHeight?: string | number;
  FieldDescription?: string;
  Err?: string;
}

function syntheticSelectChange(
  value: string,
  name?: string,
): SelectChangeEvent<string> {
  const target = { value, name } as EventTarget & {
    value: string;
    name?: string;
  };
  return {
    target,
    currentTarget: target,
  } as SelectChangeEvent<string>;
}

const SelectBox = forwardRef<any, SelectBoxProps>(function SelectBox(
  {
    value,
    onChange,
    options,
    label,
    style,
    name,
    disabled = false,
    className,
    width,
    mobileWidth,
    size = "large",
    color = "secondary",
    rtl = false,
    placeholder,
    loading,
    maxWidth,
    dropdownMaxHeight,
    FieldDescription,
    Err = "",
  },
  ref,
) {
  const innerRef = useRef<any>(null);
  useImperativeHandle(ref, () => innerRef.current, []);
  const generatedId = useId();
  const selectId = `selectbox-${generatedId.replace(/:/g, "")}`;

  const isOutline = color === "outline";
  const isOutlineLight = color === "outlineLight";
  const isOutlineSecondary = color === "outlineSecondary";
  const hasOutlineBorder = isOutline || isOutlineSecondary;

  const typographyBySize = {
    small: tokens.typography.inputSmall,
    medium: tokens.typography.inputMedium,
    large: tokens.typography.inputLarge,
  } as const;
  const labelTypographyBySize = {
    small: tokens.typography.caption,
    medium: tokens.typography.caption,
    large: tokens.typography.caption,
  } as const;
  const controlTypography = typographyBySize[size];
  const labelTypography = labelTypographyBySize[size];

  const sizeConfig = {
    small: {
      height: "30px",
      minHeight: "30px",
      borderRadius: tokens.borderRadius.md,
      padding: "0px 6px 0px 6px",
      lineHeight: String(tokens.typography.inputSmall.lineHeight),
    },
    medium: {
      height: "36px",
      minHeight: "36px",
      borderRadius: tokens.borderRadius.lg,
      padding: "0px 8px 0px 8px",
      lineHeight: String(tokens.typography.inputMedium.lineHeight),
    },
    large: {
      height: "40px",
      minHeight: "40px",
      borderRadius: tokens.borderRadius.xl,
      padding: "0px 6px 0px 6px",
      lineHeight: String(tokens.typography.inputLarge.lineHeight),
    },
  } as const;
  const currentSize = sizeConfig[size];

  /** Matches DropdownSelect: outline vs outlineSecondary for border + handle. */
  const outlineAccent = isOutlineSecondary
    ? tokens.buttonColors.outlineSecondary
    : tokens.buttonColors.outlinePrimary;

  const borderColor = hasOutlineBorder
    ? outlineAccent
    : isOutlineLight
      ? tokens.buttonColors.secondaryLight
      : outlineAccent;

  const controlColor = isOutlineLight
    ? tokens.colors.text.inverse
    : hasOutlineBorder
      ? tokens.colors.text.primary
      : tokens.colors.primary;

  const baseBackgroundColor =
    hasOutlineBorder || isOutlineLight ? "transparent" : undefined;

  const isDisabled = Boolean(disabled);

  const selectedValues = useMemo(() => {
    const opt = options.find((o) => o.value === value);
    if (!opt) return [];
    return [opt];
  }, [options, value]);

  const libraryOnChange = useCallback(
    (next: Option[]) => {
      const list = Array.isArray(next) ? next : [];
      if (list.length > 0 && list[0]) {
        onChange(syntheticSelectChange(String(list[0].value), name));
      }
    },
    [name, onChange],
  );

  const desktopWidth = toCssLength(width);
  const mobileW =
    mobileWidth != null && mobileWidth !== ""
      ? (toCssLength(mobileWidth) ?? "100%")
      : "100%";

  const wrapperClassName = [
    styles.root,
    label ? styles.wrap : null,
    label ? (rtl ? styles.wrapRtl : styles.wrapLtr) : null,
    desktopWidth !== undefined ? styles.rootDesktopWidth : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const resolvedPlaceholder = placeholder ?? "";

  const handleClosed = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      viewBox="0 0 12.529 11.249"
      aria-hidden
    >
      <path
        id="Path_91"
        data-name="Path 91"
        d="M7.563,10.5,12.326,2.25A1.5,1.5,0,0,0,11.028,0H1.5A1.5,1.5,0,0,0,.2,2.25L4.965,10.5a1.5,1.5,0,0,0,2.6,0"
        transform="translate(0)"
        fill={outlineAccent}
      />
    </svg>
  );

  const handleOpen = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      viewBox="0 0 12.529 11.249"
      aria-hidden
    >
      <path
        id="Path_91"
        data-name="Path 91"
        d="M7.563,10.5,12.326,2.25A1.5,1.5,0,0,0,11.028,0H1.5A1.5,1.5,0,0,0,.2,2.25L4.965,10.5a1.5,1.5,0,0,0,2.6,0"
        transform="translate(12.529 11.249) rotate(180)"
        fill={outlineAccent}
      />
    </svg>
  );

  const fieldDescriptionStyle = {
    fontSize: "11px",
    color: "#979797",
    transition: ".2s",
    opacity: "1",
  };
  const errStyle = {
    fontSize: "11px",
    color: "red",
    transition: ".2s",
    opacity: Err !== "" ? "1" : "0",
  };

  const selectStyle: CSSProperties & Record<string, string | number | undefined> = {
    border: hasOutlineBorder
      ? `1px solid ${borderColor}`
      : isOutlineLight
        ? `1px solid ${borderColor}`
        : "none",
    borderBottom: hasOutlineBorder
      ? `1px solid ${borderColor}`
      : isOutlineLight
        ? `1px solid ${borderColor}`
        : "1px solid #111230",
    borderRadius: currentSize.borderRadius,
    backgroundColor: baseBackgroundColor,
    color: controlColor,
    fontFamily: controlTypography.fontFamily,
    fontWeight: controlTypography.fontWeight,
    fontSize: controlTypography.fontSize,
    lineHeight: currentSize.lineHeight,
    height: currentSize.height,
    minHeight: currentSize.minHeight,
    padding: currentSize.padding,
    width: "100%",
    maxWidth: toCssLength(maxWidth) ?? maxWidth,
    cursor: isDisabled ? "not-allowed" : undefined,
    opacity: isDisabled ? 0.55 : undefined,
    transition: `background-color ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}, color ${tokens.transitions.normal}`,
    "--dropdown-scrollbar-color": tokens.colors.primary,
    "--dropdown-control-color": controlColor,
    "--dropdown-control-font-size": controlTypography.fontSize,
    "--dropdown-control-font-weight": controlTypography.fontWeight,
    "--dropdown-control-line-height": currentSize.lineHeight,
    "--dropdown-max-height": dropdownMaxHeight,
  };

  return (
    <div
      className={wrapperClassName}
      style={
        {
          ...style,
          "--ds-input-width-mobile": mobileW,
          ...(desktopWidth !== undefined
            ? { "--ds-input-width-desktop": desktopWidth }
            : {}),
        } as CSSProperties
      }
    >
      {label ? (
        <label
          htmlFor={selectId}
          className={styles.helperLabel}
          style={{
            fontFamily: labelTypography.fontFamily,
            fontSize: labelTypography.fontSize,
            fontWeight: labelTypography.fontWeight,
            lineHeight: String(labelTypography.lineHeight),
            color: tokens.colors.text.secondary,
            direction: rtl ? "rtl" : "ltr",
          }}
        >
          {label}
        </label>
      ) : null}
      <div aria-disabled={isDisabled || undefined}>
        <div className={styles.selectField}>
          <SelectAny
            id={selectId}
            disabled={isDisabled}
            values={selectedValues}
            options={options}
            onChange={libraryOnChange}
            loading={loading}
            className={styles.dropdownRoot}
            style={selectStyle}
            ref={innerRef}
            placeholder={resolvedPlaceholder}
            direction={rtl ? "rtl" : "ltr"}
            closeOnSelect
            name={name}
            dropdownHandleRenderer={({
              state,
            }: {
              state: { dropdown?: boolean };
            }) => (
              <>
                {state.dropdown ? (
                  <div style={{ position: "relative", top: "1px", left: "2px" }}>
                    {handleOpen}
                  </div>
                ) : (
                  <div style={{ position: "relative", top: "1px", left: "2px" }}>
                    {handleClosed}
                  </div>
                )}
              </>
            )}
          />
        </div>
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <div style={fieldDescriptionStyle}>{FieldDescription}</div>
          <div style={errStyle}>{Err}</div>
        </div>
      </div>
    </div>
  );
});

SelectBox.displayName = "SelectBox";

export default SelectBox;
